import React from 'react';
import type { MenuProps } from 'antd';
import { WxbButton } from '../Button/Button';
import type { WxbButtonProps } from '../Button/Button';
import { WxbDropdown } from '../Dropdown/Dropdown';
import { WxbIcon } from '../Icon/Icon';
import type { WxbIconName } from '../Icon/icons';
import { WxbIconButton } from '../IconButton/IconButton';
import { WxbPopconfirm } from '../Popconfirm/Popconfirm';
import './ActionMenu.css';

export interface WxbActionMenuConfirm {
  title: React.ReactNode;
  description?: React.ReactNode;
  okText?: string;
  cancelText?: string;
}

export interface WxbActionMenuItem {
  key: React.Key;
  label: React.ReactNode;
  icon?: WxbIconName | React.ReactNode;
  onClick?: () => void;
  danger?: boolean;
  disabled?: boolean;
  confirm?: WxbActionMenuConfirm;
}

export interface WxbActionMenuProps extends React.HTMLAttributes<HTMLDivElement> {
  actions: WxbActionMenuItem[];
  triggerLabel?: React.ReactNode;
  triggerAriaLabel?: string;
  triggerIcon?: WxbIconName | React.ReactNode;
  iconOnly?: boolean;
  buttonVariant?: WxbButtonProps['variant'];
  size?: WxbButtonProps['size'];
  placement?: 'bottomLeft' | 'bottomRight' | 'topLeft' | 'topRight';
  disabled?: boolean;
}

const joinClasses = (...classes: Array<string | false | undefined>) =>
  classes.filter(Boolean).join(' ');

const toActionKey = (key: React.Key) => String(key);

const renderMenuIcon = (icon?: WxbIconName | React.ReactNode) => {
  if (!icon) return undefined;
  return typeof icon === 'string' ? <WxbIcon name={icon} size={15} /> : icon;
};

const WxbMoreIcon = (
  <svg className="wxb-action-menu-more-icon" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="6" cy="12" r="1.5" />
    <circle cx="12" cy="12" r="1.5" />
    <circle cx="18" cy="12" r="1.5" />
  </svg>
);

export const WxbActionMenu: React.FC<WxbActionMenuProps> = ({
  actions,
  triggerLabel = '更多操作',
  triggerAriaLabel = '更多操作',
  triggerIcon,
  iconOnly = false,
  buttonVariant = 'ghost',
  size = 'sm',
  placement = 'bottomRight',
  disabled = false,
  className = '',
  ...props
}) => {
  const [pendingConfirmAction, setPendingConfirmAction] = React.useState<WxbActionMenuItem | null>(null);
  const pendingConfirm = pendingConfirmAction?.confirm;

  const menu: MenuProps = {
    items: actions.map((action) => ({
      key: toActionKey(action.key),
      label: <span className="wxb-action-menu-label">{action.label}</span>,
      icon: renderMenuIcon(action.icon),
      danger: action.danger,
      disabled: action.disabled,
    })),
    onClick: ({ key, domEvent }) => {
      domEvent.stopPropagation();
      const action = actions.find((item) => toActionKey(item.key) === key);
      if (!action || action.disabled) return;
      if (action.confirm) {
        setPendingConfirmAction(action);
        return;
      }
      action.onClick?.();
    },
  };

  const trigger = iconOnly ? (
    <WxbIconButton
      icon={triggerIcon ?? WxbMoreIcon}
      label={triggerAriaLabel}
      size={size}
      variant={buttonVariant}
      disabled={disabled}
    />
  ) : (
    <WxbButton type="button" size={size} variant={buttonVariant} disabled={disabled}>
      {triggerIcon && renderMenuIcon(triggerIcon)}
      {triggerLabel}
    </WxbButton>
  );

  return (
    <div className={joinClasses('wxb-action-menu', className)} {...props}>
      <WxbPopconfirm
        open={Boolean(pendingConfirmAction)}
        title={pendingConfirm?.title}
        description={pendingConfirm?.description}
        okText={pendingConfirm?.okText}
        cancelText={pendingConfirm?.cancelText}
        onConfirm={() => {
          pendingConfirmAction?.onClick?.();
          setPendingConfirmAction(null);
        }}
        onCancel={() => setPendingConfirmAction(null)}
        onOpenChange={(open) => {
          if (!open) setPendingConfirmAction(null);
        }}
      >
        <span className="wxb-action-menu-confirm-anchor" aria-hidden="true" />
      </WxbPopconfirm>
      <WxbDropdown menu={menu} placement={placement} trigger={['click']} disabled={disabled}>
        {trigger}
      </WxbDropdown>
    </div>
  );
};
